import type { FxDocumentRow, FxStatus } from '@/components/fx'
import { layoutLabel } from '../useCockpit'
import {
  documentStageLabel,
  STAGE_LABEL_RUNNING,
  STAGE_TITLES,
  type DocumentCounts,
  type EvaluationDocument,
  type EvaluationStatus,
  type Job,
  type MatchMetrics,
  type RecognitionArm,
  type Template,
} from '@/types/domain'

/**
 * Everything the Dokumente tab decides without touching React or the network.
 *
 * It lives in its own file because these are the parts that are wrong in ways
 * a screenshot does not show: a status that reads "noch nicht verarbeitet"
 * while the worker is on it, a filter whose count comes from a different
 * number than the rows it produces, a date that says "heute" at 23:59 in the
 * wrong timezone. All of it is tested.
 */

// -- Filters ---------------------------------------------------------------

export type DocumentFilter = 'alle' | 'unklar' | 'analysiert' | 'keinTreffer'

export const DOCUMENT_FILTERS: DocumentFilter[] = ['alle', 'unklar', 'analysiert', 'keinTreffer']

const FILTER_LABELS: Record<DocumentFilter, string> = {
  alle: 'Alle',
  unklar: 'Unklar',
  analysiert: 'Analysiert',
  keinTreffer: 'Kein Treffer',
}

/**
 * What each filter asks the gateway for.
 *
 * All four are server-side, which is the whole reason the tabs may carry
 * counts: `counts.needsReview` and `byStatus` are computed over the same
 * rows the filtered listing returns, so the number on the tab and the number
 * of rows behind it cannot disagree. (The search box below is the one thing
 * that is client-side, and it says so.)
 */
export function filterQuery(filter: DocumentFilter): {
  status?: EvaluationStatus
  needsReview?: boolean
} {
  switch (filter) {
    case 'unklar':
      return { needsReview: true }
    case 'analysiert':
      return { status: 'complete' }
    case 'keinTreffer':
      return { status: 'noMatch' }
    default:
      return {}
  }
}

/** The count behind a filter tab, or undefined while the counts are loading —
 *  a "(0)" during the first request reads as an empty form. */
export function filterCount(
  filter: DocumentFilter,
  counts?: DocumentCounts,
): number | undefined {
  if (!counts) return undefined
  switch (filter) {
    case 'alle':
      return counts.total
    case 'unklar':
      return counts.needsReview
    case 'analysiert':
      return counts.byStatus?.complete
    case 'keinTreffer':
      return counts.byStatus?.noMatch
  }
}

export function filterTabs(counts?: DocumentCounts): { value: string; label: string }[] {
  return DOCUMENT_FILTERS.map((filter) => {
    const count = filterCount(filter, counts)
    return {
      value: filter,
      label: count == null ? FILTER_LABELS[filter] : `${FILTER_LABELS[filter]} (${count})`,
    }
  })
}

export function isDocumentFilter(value: string): value is DocumentFilter {
  return (DOCUMENT_FILTERS as string[]).includes(value)
}

// -- One row ---------------------------------------------------------------

/**
 * The document ids the worker is on right now.
 *
 * A document goes to `inProgress` only once the worker picks it up, so
 * between "Analyse gestartet" and the first page being read every selected
 * row still reads `neu` — "noch nicht verarbeitet" for something that is
 * demonstrably queued. The active jobs know better, so they are asked.
 */
export function queuedDocumentIds(jobs: Job[]): Set<string> {
  const ids = new Set<string>()
  for (const job of jobs) {
    for (const id of job.document_ids ?? []) ids.add(id)
  }
  return ids
}

/**
 * The four design-system status words over the gateway's six.
 *
 * `problematic`, `noMatch` and `other` all become "unklar": each is a
 * document a person has to look at, and the row's own Unklar count, its
 * Layout cell and the Problemfälle panel say which kind it is. Inventing a
 * fifth word would mean inventing a fifth colour for it too.
 */
export function fxStatusFor(doc: EvaluationDocument, queued?: Set<string>): FxStatus {
  switch (doc.evaluationStatus) {
    case 'complete':
      return 'done'
    case 'inProgress':
      return 'inProgress'
    case 'problematic':
    case 'noMatch':
    case 'other':
      return 'unclear'
    default:
      return queued?.has(doc.id) ? 'inProgress' : 'neu'
  }
}

/** A document whose values a person has confirmed — the stage ladder's last
 *  rung, which is also what the worker writes into `stage`. */
export function isReviewedDocument(doc: EvaluationDocument): boolean {
  return (doc.stage ?? '').includes(STAGE_TITLES.human)
}

export function stageToneFor(doc: EvaluationDocument): 'neutral' | 'ai' | 'reviewed' {
  if (isReviewedDocument(doc)) return 'reviewed'
  return (doc.stage ?? '').includes('KI') ? 'ai' : 'neutral'
}

/**
 * The Layout cell.
 *
 * `matchMetrics.ambiguous` means two template layouts scored within
 * `MATCH_MIN_MARGIN` of each other — the 2018 and the 2019 form read almost
 * the same — so the page this document was read against may be the wrong
 * one. That is a fact about the value in the cell and belongs in the cell;
 * it is written as a word rather than an icon because `FxDocumentTable`'s
 * layout cell takes a string, and because a bare warning triangle names
 * nothing.
 */
export function layoutCell(template: Template | undefined, doc: EvaluationDocument): string {
  const label = layoutLabel(template, doc.matchedPage)
  return doc.matchMetrics?.ambiguous ? `${label} · unsicher` : label
}

/**
 * How many fields on this document are waiting for someone.
 *
 * `null` renders as "–" and is the honest answer for a document that has not
 * been analysed: zero would claim it was checked and found clean.
 */
export function unclearCount(doc: EvaluationDocument): number | null {
  if (doc.evaluationStatus === 'neu' || doc.evaluationStatus === 'inProgress') return null
  if (doc.evaluationStatus === 'noMatch') return null
  return doc.needsReviewCount ?? 0
}

export function documentRow(
  doc: EvaluationDocument,
  options: {
    template?: Template
    queued?: Set<string>
    selected?: boolean
    href?: string
    now?: Date
  } = {},
): FxDocumentRow {
  const status = fxStatusFor(doc, options.queued)
  return {
    id: doc.id,
    name: doc.title || 'Ohne Namen',
    date: formatUploadDate(doc.uploadedAt, options.now),
    status,
    reviewed: isReviewedDocument(doc),
    unclear: unclearCount(doc),
    stage: status === 'inProgress' ? STAGE_LABEL_RUNNING : documentStageLabel(doc),
    stageTone: stageToneFor(doc),
    layout: layoutCell(options.template, doc),
    selected: options.selected,
    href: options.href,
  }
}

// -- Dates -----------------------------------------------------------------

const DATE_FORMAT = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
})
const TIME_FORMAT = new Intl.DateTimeFormat('de-DE', { hour: '2-digit', minute: '2-digit' })

/** `uploadedAt` is MICROSECONDS since the epoch, as the worker writes it —
 *  not milliseconds. Dividing by the wrong factor puts every upload in 1970
 *  or in the year 57000, which is the kind of bug that survives review. */
export function microsToDate(micros?: number | null): Date | null {
  if (micros == null || !Number.isFinite(micros) || micros <= 0) return null
  return new Date(Math.round(micros / 1000))
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

/** "heute", "gestern" or "07.08.2026" — the artboard's own vocabulary. */
export function formatUploadDate(micros?: number | null, now: Date = new Date()): string {
  const date = microsToDate(micros)
  if (!date) return '–'
  if (sameDay(date, now)) return 'heute'
  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  if (sameDay(date, yesterday)) return 'gestern'
  return DATE_FORMAT.format(date)
}

export function formatClockTime(value?: number | string | null): string {
  if (value == null) return ''
  const date = typeof value === 'number' ? microsToDate(value) : new Date(value)
  if (!date || Number.isNaN(date.getTime())) return ''
  return TIME_FORMAT.format(date)
}

// -- The status panels -----------------------------------------------------

/**
 * The donut's four segments.
 *
 * Every gateway status is mapped, so the ring's total is the template's
 * total: a status this build has not heard of would otherwise silently
 * shrink the circle and nobody would notice the missing documents.
 */
export function donutCounts(counts?: DocumentCounts): Record<FxStatus, number> {
  const byStatus = counts?.byStatus ?? {}
  const known =
    (byStatus.neu ?? 0) +
    (byStatus.inProgress ?? 0) +
    (byStatus.complete ?? 0) +
    (byStatus.problematic ?? 0) +
    (byStatus.noMatch ?? 0) +
    (byStatus.other ?? 0)
  const unaccounted = Math.max((counts?.total ?? 0) - known, 0)
  // A document whose fields await a decision is "unklar" in the product's
  // vocabulary, but it is NOT a distinct `evaluation_status`: the worker
  // leaves it `complete` and raises `needs_review`. Reading the donut off the
  // status alone therefore drew every such document green and reported
  // "0 Formulare unklar" on a template with documents waiting — the headline
  // of the screen contradicting the red panel beside it and the "Unklar"
  // filter, both of which count `needsReview`. The flag is only ever raised on
  // an analysed document, so it moves between those two segments.
  const complete = byStatus.complete ?? 0
  const waiting = Math.min(counts?.needsReview ?? 0, complete)
  return {
    neu: (byStatus.neu ?? 0) + unaccounted,
    inProgress: byStatus.inProgress ?? 0,
    unclear: (byStatus.problematic ?? 0) + (byStatus.noMatch ?? 0) + (byStatus.other ?? 0) + waiting,
    done: complete - waiting,
  }
}

export interface UnclearSummary {
  /** The big number on the red panel. */
  value: number
  /** What that number counts, in words. */
  subject: string
  /** True while the number counts fields — the sentence the artboard draws. */
  byField: boolean
}

/**
 * The red panel's sentence: "5 Felder in 2 Dokumenten", or the honest
 * fallback.
 *
 * `counts.needsReviewFields` counts the flagged fields across the flagged
 * documents. The gateway omits the key when its aggregate fails, so
 * `undefined` means *not counted* and must never render as a zero: without a
 * field figure the panel counts documents, as it did before the endpoint
 * grew the number — a smaller sentence, not a wrong one.
 *
 * With nothing flagged at all the field count is beside the point ("0 Felder
 * in 0 Dokumenten" is a sentence nobody needs), and the panel goes on to say
 * that no field is waiting.
 */
export function unclearSummary(counts?: DocumentCounts): UnclearSummary {
  const documents = counts?.needsReview ?? 0
  const fields = counts?.needsReviewFields
  if (documents === 0 || fields == null || fields <= 0) {
    return {
      value: documents,
      subject: documents === 1 ? 'Dokument mit offenen Feldern' : 'Dokumente mit offenen Feldern',
      byField: false,
    }
  }
  return {
    value: fields,
    subject: `${fields === 1 ? 'Feld' : 'Felder'} in ${documents} ${
      documents === 1 ? 'Dokument' : 'Dokumenten'
    }`,
    byField: true,
  }
}

/** The per-layout tiles, from `counts.byMatchedPage`. `noMatch` documents are
 *  omitted by the gateway's own query, so these never sum to the total. */
export function layoutCounts(
  template: Template | undefined,
  counts?: DocumentCounts,
): { page: number; label: string; count: number }[] {
  const byPage = counts?.byMatchedPage ?? {}
  return Object.entries(byPage)
    .map(([page, count]) => ({
      page: Number(page),
      label: layoutLabel(template, Number(page)),
      count: Number(count),
    }))
    .filter((entry) => Number.isFinite(entry.page) && entry.count > 0)
    .sort((a, b) => a.page - b.page)
}

// -- The "Analyse läuft" strip --------------------------------------------

export interface RunProgress {
  /** Documents in the active jobs. */
  total: number
  done: number
  processing: number
  queued: number
  /** 0..100, the worker's own figure averaged over the active jobs. */
  percent: number
  startedAt: string
}

/**
 * What the progress bar knows.
 *
 * `GET /v1/jobs/:id` carries one number — `progress`, a percentage the worker
 * writes — and no processed-page count. So "26 von 31 fertig" is arithmetic:
 * the jobs' own document count times their progress. It is labelled as an
 * estimate ("etwa") for that reason; the exact figures on the screen are the
 * status counts, which are counted rows.
 */
export function runProgress(activeJobs: Job[], counts?: DocumentCounts): RunProgress | null {
  if (activeJobs.length === 0) return null
  const total = activeJobs.reduce((sum, job) => sum + (job.document_ids?.length ?? 0), 0)
  const weighted = activeJobs.reduce(
    (sum, job) => sum + (job.progress ?? 0) * (job.document_ids?.length ?? 0),
    0,
  )
  const percent = total > 0 ? Math.min(Math.max(Math.round(weighted / total), 0), 100) : 0
  const done = Math.round((total * percent) / 100)
  const processing = Math.min(counts?.byStatus?.inProgress ?? 0, Math.max(total - done, 0))
  const queued = Math.max(total - done - processing, 0)
  const started = activeJobs
    .map((job) => job.started_at ?? job.created_at)
    .filter((value): value is string => !!value)
    .sort()[0]
  return { total, done, processing, queued, percent, startedAt: formatClockTime(started) }
}

// -- Problem cases ---------------------------------------------------------

/** The template page that came closest, as `engine.py` records it on a
 *  no-match (`matchMetrics.page`). Not declared on `MatchMetrics` in
 *  `@/types/domain` — reported — so it is read defensively. */
export function bestPageOf(metrics?: MatchMetrics): number | undefined {
  const page = (metrics as (MatchMetrics & { page?: number }) | undefined)?.page
  return typeof page === 'number' && page > 0 ? page : undefined
}

/** German decimals, two places: "0,18". */
export function formatSimilarity(similarity?: number | null): string | null {
  if (similarity == null || !Number.isFinite(similarity)) return null
  return similarity.toFixed(2).replace('.', ',')
}

/**
 * Why no template page fit.
 *
 * The gateway already turns the first `matchMetrics.reasons` entry into a
 * German label (`internal/api/matching_reasons.go`), so that is what is
 * shown. The fallback is deliberately vague rather than inventing a cause.
 */
export function noMatchReasonOf(doc: EvaluationDocument): string {
  return doc.noMatchReason || 'Keiner Seite dieser Vorlage zuzuordnen.'
}

// -- The arm runs ----------------------------------------------------------

export interface ArmCopy {
  arm: RecognitionArm
  /** The button, and the dialog's title. */
  label: string
  /** What the model gets to see. */
  what: string
}

export const ARM_RUNS: Record<'llm_text' | 'llm_image', ArmCopy> = {
  llm_text: {
    arm: 'llm_text',
    label: 'KI-Textprüfung',
    what: 'Die KI sieht nur den erkannten Text, keine Seite verlässt dabei zusätzlich den Server.',
  },
  llm_image: {
    arm: 'llm_image',
    label: 'KI-Bildprüfung',
    what: 'Die KI sieht die Seite selbst und die eingezeichneten Felder.',
  },
}

/**
 * The sentence under the chosen option — the one thing on this screen that
 * must not be vague.
 *
 * `apply` defaults to **true** in the worker: a run started without it
 * rewrites the delivered values, and the next run then reads its own output
 * as the OCR baseline, agrees with itself and reports full confidence on a
 * wrong value. So both options are spelled out and the value is always sent.
 */
export function applyExplanation(apply: boolean, count: number): string {
  const docs = documentCountLabel(count)
  return apply
    ? `Die neue Lesung ersetzt die ausgelieferten Werte von ${docs}. Die bisherige OCR-Lesung bleibt als Vergleich gespeichert.`
    : `Die Lesung wird neben der bisherigen gespeichert. Die ausgelieferten Werte von ${docs} bleiben unverändert.`
}

// -- Small words -----------------------------------------------------------

export function documentCountLabel(count: number): string {
  return count === 1 ? '1 Dokument' : `${count} Dokumenten`
}

export function documentCountSubject(count: number): string {
  return count === 1 ? '1 Dokument' : `${count} Dokumente`
}

export function pageCountOf(total: number, pageSize: number): number {
  if (pageSize <= 0) return 1
  return Math.max(1, Math.ceil(total / pageSize))
}

/**
 * The search box's client-side filter.
 *
 * `GET /documents` has no `q`, so this can only see the page that is loaded.
 * That is not a box to ship unlabelled — over a 400-document template it
 * would miss nearly everything — so the screen names its scope beside it and
 * reports how much it searched.
 */
export function searchLoaded(
  docs: EvaluationDocument[],
  query: string,
): EvaluationDocument[] {
  const needle = query.trim().toLowerCase()
  if (needle === '') return docs
  return docs.filter((doc) => (doc.title ?? '').toLowerCase().includes(needle))
}
